import React, { useEffect, useState } from 'react';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { fetchTenantOptions } from '../api/userManagement';

export default function TenantPickerForm({
  currentTenantId = null,
  onSubmit,
  onSelectionChange,
  submitLabel = 'Use tenant',
  showSubmitButton = true,
  buttonVariant = 'contained',
  buttonSx,
  size = 'medium',
  helperText = 'Choose the tenant context to work in.',
  label = 'Tenant',
  required = false,
}) {
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(currentTenantId ? String(currentTenantId) : '');
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setSelectedTenantId(currentTenantId ? String(currentTenantId) : '');
  }, [currentTenantId]);

  useEffect(() => {
    let active = true;

    const loadTenantOptions = async () => {
      setIsLoadingOptions(true);
      setOptionsError('');

      try {
        const options = await fetchTenantOptions();
        if (!active) {
          return;
        }
        setTenantOptions(options);
      } catch (error) {
        if (!active) {
          return;
        }
        setOptionsError(error.message || 'Failed to load tenant options.');
      } finally {
        if (active) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadTenantOptions();

    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedTenantId) {
      setSubmitError('Please choose a tenant.');
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);

    try {
      await onSubmit(Number(selectedTenantId));
      setSubmitError('');
    } catch (error) {
      setSubmitError(error.message || 'Failed to switch tenant.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTenantChange = async (event) => {
    const nextTenantId = event.target.value;

    setSelectedTenantId(nextTenantId);
    setSubmitError('');

    if (!onSelectionChange) {
      return;
    }

    try {
      await onSelectionChange(nextTenantId ? Number(nextTenantId) : null);
    } catch (error) {
      setSubmitError(error.message || 'Failed to apply the selected tenant.');
    }
  };

  return (
    <Stack component={showSubmitButton ? 'form' : 'div'} spacing={2} onSubmit={showSubmitButton ? handleSubmit : undefined}>
      <TextField
        select
        label={label}
        value={selectedTenantId}
        onChange={handleTenantChange}
        disabled={isLoadingOptions || tenantOptions.length === 0 || isSubmitting}
        helperText={isLoadingOptions ? 'Loading tenant options...' : helperText}
        fullWidth
        size={size}
        required={required}
      >
        {tenantOptions.map((tenant) => (
          <MenuItem key={tenant.id} value={String(tenant.id)}>
            {tenant.company}
          </MenuItem>
        ))}
      </TextField>

      {optionsError && <Alert severity="error">{optionsError}</Alert>}
      {submitError && <Alert severity="error">{submitError}</Alert>}
      {!optionsError && !isLoadingOptions && tenantOptions.length === 0 && (
        <Alert severity="info">No tenant options are available yet.</Alert>
      )}

      {showSubmitButton && (
        <Button
          type="submit"
          variant={buttonVariant}
          disabled={isLoadingOptions || tenantOptions.length === 0 || isSubmitting}
          sx={buttonSx}
        >
          {isSubmitting ? 'Applying...' : submitLabel}
        </Button>
      )}
    </Stack>
  );
}
